import {
  CATEGORY,
  BRAND,
  CONDITION,
  PRODUCT_TYPE,
  CHANNEL,
  CUSTOM_LABEL,
  CONDITION_NEW,
  CONDITION_USED,
  CONDITION_REFURBISHED,
  CHANNEL_ONLINE,
  CHANNEL_LOCAL,
  ITEM_ID,
} from "../i18n/keysTranslations";
import { PRODUCTS_CATEGORIES_GOOGLE } from "../utils/options";

/**
 * GOOGLE ONLY: Utilities to aggregate multipleConversions by category-origin.
 * Aligned with SelectConversionActionsGoogle grouping logic.
 */

const MULTIPLE_CONVERSION_PREFIX = "multipleConversion:";

/**
 * Builds id -> { category, origin } map from conversion actions.
 * @param {Array} actions - conversionActions from platformsData.google
 * @returns {Object} Map of id to { category, origin }
 */
export const getConversionActionMap = (actions) => {
  const map = {};
  (actions || []).forEach((a) => {
    if (a?.id && a.category && a.origin) {
      map[String(a.id)] = { category: a.category, origin: a.origin };
    }
  });
  return map;
};

/**
 * Aggregates multipleConversions array by category-origin, returns { [metricKey]: sum }.
 * @param {Array} multipleConversions - [{ value, id?, label? }]
 * @param {Object} idToCategoryOrigin - From getConversionActionMap(conversionActions)
 * @returns {Object} { "multipleConversion:PURCHASE-WEBSITE": 24.1, ... }
 */
export const aggregateMultipleConversionsByGroup = (
  multipleConversions,
  idToCategoryOrigin
) => {
  const grouped = {};
  const arr = Array.isArray(multipleConversions) ? multipleConversions : [];

  arr.forEach((item) => {
    const value =
      typeof item?.value === "number" ? item.value : parseFloat(item?.value);
    if (value !== value) return;

    let groupKey;
    if (item?.id != null) {
      const mapped = idToCategoryOrigin[String(item.id)];
      if (mapped) {
        groupKey = `${mapped.category}-${mapped.origin}`;
      } else {
        groupKey = item?.label ?? String(item.id);
      }
    } else {
      groupKey = item?.label;
    }
    if (groupKey == null) return;

    const metricKey = `${MULTIPLE_CONVERSION_PREFIX}${groupKey}`;
    grouped[metricKey] = (grouped[metricKey] ?? 0) + value;
  });

  return grouped;
};

/**
 * Enriches a statistics object with grouped multipleConversions (mutates in place).
 * Removes raw multipleConversions to avoid duplicates.
 * @param {Object} statsObj - Campaign, assetsGroup, adGroup, or ad statistics
 * @param {Array} conversionActions - From platformsData.google.conversionActions
 */
export const enrichStatsWithGroupedMultipleConversions = (
  statsObj,
  conversionActions
) => {
  if (!statsObj || !Array.isArray(statsObj.multipleConversions)) return;

  const idToCategoryOrigin = getConversionActionMap(conversionActions);
  const aggregated = aggregateMultipleConversionsByGroup(
    statsObj.multipleConversions,
    idToCategoryOrigin
  );

  Object.assign(statsObj, aggregated);
  delete statsObj.multipleConversions;
};

/**
 * Enriches Google statistics at all levels (campaign, assetsGroups, adsGroups, ads, keywords, webpages).
 * @param {Object} statistics - statistics[GOOGLE]
 * @param {Array} conversionActions - From campaign.platformsData.google.conversionActions
 */
export const enrichGoogleStatisticsWithMultipleConversions = (
  statistics,
  conversionActions
) => {
  if (!statistics || !conversionActions?.length) return;

  if (statistics.campaign) {
    enrichStatsWithGroupedMultipleConversions(
      statistics.campaign,
      conversionActions
    );
  }

  if (statistics.assetsGroups) {
    Object.values(statistics.assetsGroups).forEach((stats) =>
      enrichStatsWithGroupedMultipleConversions(stats, conversionActions)
    );
  }

  if (statistics.adsGroups) {
    Object.values(statistics.adsGroups).forEach((stats) =>
      enrichStatsWithGroupedMultipleConversions(stats, conversionActions)
    );
  }

  if (statistics.ads) {
    Object.values(statistics.ads).forEach((stats) =>
      enrichStatsWithGroupedMultipleConversions(stats, conversionActions)
    );
  }

  if (statistics.keywords) {
    Object.values(statistics.keywords).forEach((keywordMap) => {
      if (typeof keywordMap === "object" && keywordMap !== null) {
        Object.values(keywordMap).forEach((stats) => {
          if (stats && typeof stats === "object") {
            enrichStatsWithGroupedMultipleConversions(stats, conversionActions);
          }
        });
      }
    });
  }

  if (statistics.webpages) {
    Object.values(statistics.webpages).forEach((webpageMap) => {
      if (typeof webpageMap === "object" && webpageMap !== null) {
        Object.values(webpageMap).forEach((stats) => {
          if (stats && typeof stats === "object") {
            enrichStatsWithGroupedMultipleConversions(stats, conversionActions);
          }
        });
      }
    });
  }
};

/**
 * Enriches all Google statistics in a getFromStatisticsAdsCampaign result.
 * @param {Object} result - Return value from getFromStatisticsAdsCampaign
 * @param {Object} campaignData - Campaign with platformsData
 */
export const enrichResultWithGoogleMultipleConversions = (
  result,
  campaignData
) => {
  const conversionActions =
    campaignData?.platformsData?.["google"]?.conversionActions || [];
  if (!conversionActions.length) return;

  const enrichPlatformStats = (statsByPlatform) => {
    if (!statsByPlatform?.google) return;
    enrichGoogleStatisticsWithMultipleConversions(
      statsByPlatform.google,
      conversionActions
    );
  };

  if (result.statistics) {
    enrichPlatformStats(result.statistics);
  }
  if (result.previousPeriodStatistics) {
    enrichPlatformStats(result.previousPeriodStatistics);
  }
  if (result.historical) {
    Object.values(result.historical).forEach(enrichPlatformStats);
  }
  if (result.historicalPreviousPeriodStatistics) {
    Object.values(result.historicalPreviousPeriodStatistics).forEach(
      enrichPlatformStats
    );
  }
};

/**
 * Gets translated group label for category-origin (e.g. "Compra - Sitio Web").
 * GOOGLE ONLY. Splits on first hyphen to support origins like "GOOGLE_HOSTED".
 * @param {string} groupKey - e.g. "PURCHASE-WEBSITE"
 * @param {Function} t - i18n translate function
 */
export const getTranslatedGroupName = (groupKey, t) => {
  const idx = groupKey.indexOf("-");
  const category = idx >= 0 ? groupKey.slice(0, idx) : groupKey;
  const origin = idx >= 0 ? groupKey.slice(idx + 1) : "DEFAULT";
  return `${t(category) || category} - ${t(origin) || origin}`;
};

export { MULTIPLE_CONVERSION_PREFIX };

export const SUB_DIVISIONS_WITH_COUNT_GOOGLE = [
  "productCategory",
  "productBrand",
  "condition",
  "productType",
  "customLabel0",
  "customLabel1",
  "customLabel2",
  "customLabel3",
  "customLabel4",
];
// Mock data for subdivisions and their options
export const SUBDIVISION_OPTIONS_GOOGLE = [
  {
    value: "productCategory",
    label: CATEGORY,
    translateOptions: true,
    options: PRODUCTS_CATEGORIES_GOOGLE,
  },
  {
    value: "productBrand",
    label: BRAND,
    translateOptions: false,
    options: [],
  },
  { value: "itemId", translateOptions: false, label: ITEM_ID, options: [] },
  {
    value: "condition",
    label: CONDITION,
    translateOptions: true,
    options: [
      { label: CONDITION_NEW, value: "NEW" },
      { label: CONDITION_USED, value: "USED" },
      { label: CONDITION_REFURBISHED, value: "REFURBISHED" },
    ],
  },
  {
    value: "productType",
    translateOptions: false,
    label: PRODUCT_TYPE,
    options: [],
  },
  {
    value: "channel",
    translateOptions: true,
    label: CHANNEL,
    options: [
      { label: CHANNEL_ONLINE, value: "ONLINE" },
      { label: CHANNEL_LOCAL, value: "LOCAL" },
    ],
  },
  {
    value: "customLabel0",
    translateOptions: false,
    label: CUSTOM_LABEL,
    subLabel: "0",
    options: [],
  },
  {
    value: "customLabel1",
    translateOptions: false,
    label: CUSTOM_LABEL,
    subLabel: "1",
    options: [],
  },
  {
    value: "customLabel2",
    translateOptions: false,
    label: CUSTOM_LABEL,
    subLabel: "2",
    options: [],
  },
  {
    value: "customLabel3",
    translateOptions: false,
    label: CUSTOM_LABEL,
    subLabel: "3",
    options: [],
  },
  {
    value: "customLabel4",
    translateOptions: false,
    label: CUSTOM_LABEL,
    subLabel: "4",
    options: [],
  },
];
